import { Badge } from '@/components/ui'
import { Check, X, Clock, History } from 'lucide-react'
import { formatDateTime } from '@/lib/timeFormatters'
import type { WorkflowRunApprovalRequest } from '@/types'

interface Props {
  approvalRequests: WorkflowRunApprovalRequest[]
}

const statusConfig = {
  Pending: { icon: Clock, label: 'Pending', className: 'bg-warning text-warning-foreground border-warning-foreground' },
  Approved: { icon: Check, label: 'Approved', className: 'bg-success text-success-foreground border border-success-foreground' },
  Rejected: { icon: X, label: 'Rejected', className: 'bg-danger text-danger-foreground border border-danger-foreground' },
  Superseded: { icon: History, label: 'Superseded', className: 'bg-neutral text-neutral-foreground border border-neutral-foreground' },
} as const

export function ApprovalHistorySection({ approvalRequests }: Props) {
  if (approvalRequests.length === 0) return null

  return (
    <div className="rounded-lg border bg-card" data-testid="approval-history-section">
      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-4 pt-4 pb-2">
        Approval History
      </div>
      <div className="px-4 pb-4 space-y-3">
        {approvalRequests.map((request) => {
          const config = statusConfig[request.status] ?? statusConfig.Pending
          const Icon = config.icon

          return (
            <div key={request.id} className="flex items-start gap-3 text-sm">
              <div className="mt-0.5">
                <Icon className={`h-4 w-4 ${
                  request.status === 'Approved' ? 'text-success-foreground' :
                  request.status === 'Rejected' ? 'text-danger-foreground' :
                  request.status === 'Superseded' ? 'text-neutral-foreground' :
                  'text-warning-foreground animate-pulse'
                }`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{request.phaseName}</span>
                  <Badge variant="outline" className={`text-xs ${config.className}`}>
                    {config.label}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {request.status === 'Pending' ? (
                    'Awaiting decision'
                  ) : request.status === 'Superseded' ? (
                    'Replaced by a newer request'
                  ) : (
                    <>
                      by {request.decidedBy} on {formatDateTime(request.decidedAt)}
                    </>
                  )}
                </div>
                {request.comment && (
                  <p className="text-xs text-muted-foreground mt-1 italic">
                    &ldquo;{request.comment}&rdquo;
                  </p>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
